import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, ChevronRight, Shield } from 'lucide-react';

const LOGO_WHITE_URL = "https://customer-assets.emergentagent.com/job_pms-provence-garden/artifacts/7ftdz3p1_Jardily-blanc.png";

// Service/City Ticker Component - Scrolling Marquee
const ServiceCityTicker = () => {
  const services = [
    "Tonte de pelouse", "Taille de haies", "Débroussaillage", "Élagage d'arbres",
    "Désherbage", "Arrosage automatique", "Aménagement paysager", "Entretien de jardin"
  ];
  const cities = [
    "Istres", "Fos-sur-Mer", "Miramas", "Martigues", "Port-de-Bouc",
    "Salon-de-Provence", "Vitrolles", "Marignane", "Berre-l'Étang", "Saint-Chamas",
    "Grans", "Cornillon-Confoux", "Saint-Mitre-les-Remparts", "Châteauneuf-les-Martigues",
    "Carry-le-Rouet", "Sausset-les-Pins", "Port-Saint-Louis-du-Rhône", "Arles",
    "Les Pennes-Mirabeau", "Coudoux"
  ];

  // Generate all combinations
  const items = [];
  services.forEach(service => {
    cities.forEach(city => {
      items.push({ service, city });
    });
  });

  // Shuffle and take a subset for display
  const displayItems = items.sort(() => Math.random() - 0.5).slice(0, 30);

  return (
    <div className="bg-stone-900/90 backdrop-blur-sm py-3 overflow-hidden" data-testid="service-ticker">
      <div className="relative">
        <div className="flex animate-marquee-slow whitespace-nowrap">
          {displayItems.map((item, index) => (
            <span key={index} className="inline-flex items-center gap-2 mx-8 text-sm">
              <span className="text-emerald-400 font-medium">{item.service}</span>
              <span className="text-stone-500">sur</span>
              <span className="flex items-center gap-1 text-white font-medium">
                <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                {item.city}
              </span>
            </span>
          ))}
          {/* Duplicate for seamless loop */}
          {displayItems.map((item, index) => (
            <span key={`dup-${index}`} className="inline-flex items-center gap-2 mx-8 text-sm">
              <span className="text-emerald-400 font-medium">{item.service}</span>
              <span className="text-stone-500">sur</span>
              <span className="flex items-center gap-1 text-white font-medium">
                <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                {item.city}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// French flag ribbon component
const FrenchRibbon = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="group flex items-center transition-all duration-300"
      >
        {/* Flag ribbon */}
        <div className="flex flex-col w-3 h-16 rounded-r-md overflow-hidden shadow-lg hover:w-4 transition-all">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
        
        {/* Expanded content */}
        <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-w-xs opacity-100' : 'max-w-0 opacity-0'}`}>
          <div className="bg-white rounded-r-xl shadow-xl px-4 py-3 ml-0 whitespace-nowrap border-l-0">
            <p className="font-heading font-bold text-stone-900 text-sm">Startup française</p>
            <p className="text-emerald-600 text-xs font-medium">et fière de l'être !</p>
          </div>
        </div>
      </button>
    </div>
  );
};

const Footer = () => {
  return (
    <>
      <FrenchRibbon />
      <ServiceCityTicker />
      
      <footer className="bg-emerald-900 text-white" data-testid="footer">
        {/* Wave decoration */}
        <div className="footer-wave"></div>
        
        <div className="container-custom py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <img 
                src={LOGO_WHITE_URL} 
                alt="Jardily" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-emerald-200 text-sm mb-4">
                Votre partenaire pour un jardin impeccable à Istres et ses environs.
              </p>
              <p className="text-2xl font-heading font-bold text-white">
                avoir un <span className="text-emerald-300">BEAU JARDIN</span> c'est{' '}
                <span className="font-accent text-emerald-300">EASY</span>*
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Navigation</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Accueil
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Nos services
                  </Link>
                </li>
                <li>
                  <Link to="/entretien" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Entretien de jardin
                  </Link>
                </li>
                <li>
                  <Link to="/amenagement" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Aménagement paysager
                  </Link>
                </li>
                <li>
                  <Link to="/elagage" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Élagage
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Blog & Conseils
                  </Link>
                </li>
                <li>
                  <Link to="/credit-impot" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Crédit d'impôts 50%
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-emerald-200 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Nos services</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/services#tonte-pelouse" className="text-emerald-200 hover:text-white transition-colors text-sm">
                    Tonte de pelouse
                  </Link>
                </li>
                <li>
                  <Link to="/services#taille-haies" className="text-emerald-200 hover:text-white transition-colors text-sm">
                    Taille de haies
                  </Link>
                </li>
                <li>
                  <Link to="/services#debroussaillage" className="text-emerald-200 hover:text-white transition-colors text-sm">
                    Débroussaillage
                  </Link>
                </li>
                <li>
                  <Link to="/services#arrosage-automatique" className="text-emerald-200 hover:text-white transition-colors text-sm">
                    Arrosage automatique
                  </Link>
                </li>
                <li>
                  <Link to="/services#elagage" className="text-emerald-200 hover:text-white transition-colors text-sm">
                    Élagage d'arbres
                  </Link>
                </li>
                <li>
                  <Link to="/services#amenagement-paysager" className="text-emerald-200 hover:text-white transition-colors text-sm">
                    Aménagement paysager
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="tel:0465011022" 
                    className="flex items-center gap-3 text-emerald-200 hover:text-white transition-colors text-sm"
                    data-testid="footer-phone"
                  >
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    04 65 01 10 22
                  </a>
                </li>
                <li>
                  <a 
                    href="mailto:contact@jardily.fr" 
                    className="flex items-center gap-3 text-emerald-200 hover:text-white transition-colors text-sm"
                    data-testid="footer-email"
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    contact@jardily.fr
                  </a>
                </li>
                <li className="flex items-start gap-3 text-emerald-200 text-sm">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Istres, Provence
                </li>
              </ul>
              
              {/* Social Links */}
              <div className="flex gap-4 mt-6">
                <a 
                  href="#" 
                  className="w-10 h-10 rounded-full bg-emerald-800 flex items-center justify-center hover:bg-emerald-700 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 rounded-full bg-emerald-800 flex items-center justify-center hover:bg-emerald-700 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 rounded-full bg-emerald-800 flex items-center justify-center hover:bg-emerald-700 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar with French flag accent */}
          <div className="mt-12 pt-8 border-t border-emerald-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
              <div className="flex items-center gap-4">
                <p className="text-emerald-300 text-sm text-center md:text-left">
                  © {new Date().getFullYear()} Jardily - PMS Provence Multi Service. Tous droits réservés.
                </p>
                {/* Small French flag */}
                <div className="hidden md:flex items-center gap-1 bg-emerald-800/50 px-2 py-1 rounded-full">
                  <div className="flex h-3 rounded-sm overflow-hidden">
                    <div className="w-2 bg-[#002395]"></div>
                    <div className="w-2 bg-white"></div>
                    <div className="w-2 bg-[#ED2939]"></div>
                  </div>
                  <span className="text-emerald-300 text-xs">Made in France</span>
                </div>
              </div>
              <p className="text-emerald-400 text-xs" data-testid="easy-translation">
                *EASY = Facile
              </p>
            </div>
            {/* Legal links and version */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm">
                <Link to="/mentions-legales" className="text-emerald-400 hover:text-white transition-colors">
                  Mentions légales
                </Link>
                <span className="text-emerald-700">|</span>
                <Link to="/politique-confidentialite" className="text-emerald-400 hover:text-white transition-colors">
                  Politique de confidentialité
                </Link>
                <span className="text-emerald-700">|</span>
                <Link to="/cgv" className="text-emerald-400 hover:text-white transition-colors">
                  CGV
                </Link>
              </div>
              {/* Version info + Admin link */}
              <div className="flex items-center justify-center md:justify-end gap-3">
                <Link 
                  to="/admin/login" 
                  className="text-emerald-700 hover:text-emerald-400 transition-colors"
                  data-testid="admin-login-link"
                  title="Espace pro"
                >
                  <Shield className="w-4 h-4" />
                </Link>
                <p className="text-emerald-600/50 text-[10px] font-mono" data-testid="version-info">
                  v1.3.0 • Build : 31/03/2026 à 17:49
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
